import gql from 'graphql-tag';
import ApolloClient from 'apollo-boost';
import {getTimestampInTimeZone} from '@utilities/date';
import moment from 'moment';
import {getCurrency, getLanguage} from '@utilities/helpers';
import * as Sentry from '@sentry/browser';


const Backoffice = {
    graphQLClient: null,
};

export default {
    bindAPIClient(use_token = false) {
        const token = this.getLoginToken();
        const headers = {};
        if(use_token && token){
            headers.authorization = 'Bearer ' + token;
        }
        Backoffice.graphQLClient = new ApolloClient({
            uri: window.APP.backoffice.url + '/api/graphql',
            headers: headers,
            onError: ({graphQLErrors, networkError}) => {
                if (networkError && networkError.statusCode && (networkError.statusCode < 200 || networkError.statusCode >= 300)) {
                    Sentry.captureException(new Error(`Network error: ${networkError.message}`), {
                        extra: {
                            graphQLErrors,
                            networkError,
                        },
                    });
                }
            },
        });
        return Backoffice.graphQLClient;
    },

    getLoginToken() {
        return localStorage.getItem('api-token');
    },

    removeLoginToken() {
        return localStorage.removeItem('api-token');
    },

    async loginAnonUser() {
        return this.login({
            'username': window.APP.backoffice.user,
            'password': window.APP.backoffice.password,
        }, true);
    },

    async isTokenValid(){
        try{
            const response = await this.getCurrentUser();
            if(!response.data.me.id){
                throw Error('token_expired');
            }
        }
        catch(error) {
            return false;
        }
        return true;
    },

    async checkUserTokenBeforeOrder(isUserLoggedIn) {
        /*
            czy uzytkownik zalogowany
            TAK:
                - czy token usera jest aktualny: uzyc/zalogowac ponownie uzytkownika
            NIE:
                - zalogować użytkownika anon
        */

        if(isUserLoggedIn){
            return await this.isTokenValid();
        }
        else{
            await this.loginAnonUser();
        }
        return true;
    },

    async login(params, retry = false) {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': '*/*' },
            body: JSON.stringify(params),
        };
        const response = await fetch(window.APP.backoffice.url+'/api/login', requestOptions);
        if(response.status !== 200){
            if(retry){
                try{
                    await this.login(params);
                }
                // eslint-disable-next-line no-empty
                catch(err){}
            }
            const responseBody = await response.text();
            throw new Error('Login failed: ' + responseBody);
        }
        const responseJSON =  await response.json();
        if(responseJSON.status === true){
            localStorage.setItem('api-token', responseJSON.bearer_token);
        }
        return responseJSON;
    },

    async getCurrentUser() {
        this.bindAPIClient(true);
        const response = await Backoffice.graphQLClient.query({
            query: gql`query me {
                me {
                    id
                    email
                    login
                    firstName
                    lastName
                    phoneNumber
                    roles
                    status
                    department
                    position
                    isPushNotificationsActive
                    clientProfile {
                        client{nip,individualName,corporateName,addressStreet,addressBuildingNumber,addressApartmentNumber,addressPostal,addressCity,addressCountry}
                    }
                    hasRequiredConsents
                    consents
                }
            }`,
        });
        this.bindAPIClient(false);
        return response;
    },

    //query: gql`query searchMapAutocomplete($searchMapPlaceInput: SearchMapPlaceInput!) {
    //                 searchMapAutocomplete(searchMapPlaceInput: $searchMapPlaceInput) {

    async clientPlannerRidesList(limit, offset, first, last, filter, orderBy) {
        this.bindAPIClient(true);
        const response = await Backoffice.graphQLClient.query({
            query: gql`query clientPlannerRidesList($limit: Int, $offset: Int, $first: Int, $last: Int, $filter: ClientPlannerRidesListQueryFilterInput, $orderBy: ClientPlannerRidesListQueryOrderByInput) {
                clientPlannerRidesList {
                    clientPlannerRidesList(limit: $limit, offset: $offset, first: $first, last: $last, filter: $filter, orderBy: $orderBy) {
                        totalCount
                        pageInfo {hasNextPage,hasPreviousPage,startCursor,endCursor}
                        edges {
                            cursor,node{order{id,number,status},start,end,status,type,startStop{address},endStop{address},rideStops{address}}
                        }
                    }
                }
            }`,
            variables: {
                limit: limit,
                offset: offset,
                // first: first,
                // last: last,
                filter: filter,
                orderBy: orderBy,
            },
        });
        this.bindAPIClient(false);
        return response;
    },

    async orderByClient(orderId) {
        this.bindAPIClient(true);
        const response = await Backoffice.graphQLClient.query({
            query: gql`query orderByClient($orderId: String!) {
                orderByClient(orderId: $orderId) {
                    id
                    number
                    rideType
                    status
                    requireInvoice
                    rideCyclic {
                        days
                        startDate
                        endDate
                    }
                    rideCharacter
                    passengers {
                        firstName
                        lastName
                        email
                        phoneNumber
                        type
                    }
                    rides {
                        stops {addressAddress,date}
                        ticketedDataStartStopPoint{address}
                        ticketedDataEndStopPoint{address}
                        ticketedDataStartStopAt
                        ticketedDataEndStopAt
                        passengers {
                            firstName
                            lastName
                            email
                            phoneNumber
                            type
                        }
                        comment
                        vehiclePreferredId
                        flightNumber
                        flightDate
                        additionalOptionsList {additionalOption{name},count,pricePerUnit}
                        ticket {file{id}}
                        isEditAvailable
                    }
                    value {
                        currency
                        currencyConversionRate
                        grossPriceWithoutDiscount
                        grossPriceWithDiscount
                        discountValue
                        netPriceWithDiscount
                        vat
                        voucher {type,valueAmount,valuePercentage}
                    }
                    paymentOption
                    paymentStatus
                    commentForClient
                    createdAt
                    invoice {
                        file {id}
                    }
                    payment{id,payment{status,source,valueCurrency,valueAmount}}
                }
            }`,
            variables: {
                orderId: orderId,
            },
        });
        this.bindAPIClient(false);
        return response;
    },

    async editClientUserMe(params) {
        this.bindAPIClient(true);
        const response = await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation editClientUserMe($EditClientUserMeInput: EditClientUserMeInput!) {
                editClientUserMe(EditClientUserMeInput: $EditClientUserMeInput) {
                    id
                }
            }`,
            variables: {
                EditClientUserMeInput: params,
            },
        });
        this.bindAPIClient(false);
        return response;
    },

    async addOrderByClient(type, params, redirect = '') {
        this.bindAPIClient(true);
        const addOrderByClientInput = Object.assign(params,{
            'rideType': type,
            'rideCharacter': 'SHARED',
            'acquisitionSource': 'BOOKING_PAGE',
            'paymentExternalRedirect': ('http://localhost:3000'===window.location.origin?'http://booking.fm.site:8080':window.location.origin) + redirect,
        });
        const response = await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation addOrderByClient($addOrderByClientInput: AddOrderByClientInput!) {
                addOrderByClient(addOrderByClientInput: $addOrderByClientInput) {
                    id,number,payment{payment{appUrl}}
                }
            }`,
            variables: {
                addOrderByClientInput: addOrderByClientInput,
            },
        });
        this.bindAPIClient(false);
        return response;
    },

    async searchMapAutocomplete(input, cur_lat = null, cur_lng = null) {
        const resp = await Backoffice.graphQLClient.query({
            query: gql`query searchMapAutocomplete($searchMapPlaceInput: SearchMapPlaceInput!) {
                searchMapAutocomplete(searchMapPlaceInput: $searchMapPlaceInput) {
                    description
                    types
                    placeId
                }
            }`,
            variables: {
                searchMapPlaceInput: {
                    'input': input,
                    'language': window.APP.locale,
                    'currentLatitude': cur_lat,
                    'currentLongitude': cur_lng,
                    'radius': 10,
                    'onlyAddresses': true,
                },
            },
        });
        return resp.data.searchMapAutocomplete;
    },

    async searchMapPlaceDetails(placeId) {
        const resp = await Backoffice.graphQLClient.query({
            query: gql`query searchMapPlaceDetails($placeId: String!,$language: String) {
                searchMapPlaceDetails(placeId: $placeId,language: $language) {
                    locationLatitude,locationLongitude,name,address,cityCode,postalCode
                }
            }`,
            variables: {
                placeId: placeId,
                language: window.APP.locale,
            },
        });
        return resp.data.searchMapPlaceDetails;
    },

    async registerClientUser(data, individualAccount = true, consentsChecked = true) {
        const RegisterClientUserInput = Object.assign(data,{
            'type': individualAccount?'INDIVIDUAL':'CORPORATE',
            'isContent': consentsChecked,
        });
        return await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation registerClientUser($RegisterClientUserInput: RegisterClientUserInput!){
                registerClientUser (RegisterClientUserInput: $RegisterClientUserInput){
                    id
                }
            }`,
            variables: {
                RegisterClientUserInput: RegisterClientUserInput,
            },
        });
    },

    async requestResetClientPassword(email) {
        return await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation requestResetClientPassword(
                $email: String!,
                $externalRedirect: String
            ) {
                requestResetClientPassword(
                    email: $email,
                    externalRedirect: $externalRedirect
                )
            }`,
            variables: {
                email: email,
                externalRedirect: ('http://localhost:3000'===window.location.origin?'http://booking.fm.site:8080':window.location.origin) + '/reset-password',
            },
        });
    },

    async resetUserPassword(token, password) {
        return await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation resetUserPassword(
                $token: String!,
                $password: String!
            ) {
                resetUserPassword(
                    token: $token,
                    password: $password
                )
            }`,
            variables: {
                token: token,
                password: password,
            },
        });
    },

    async activateByClientToken(email, token) {
        return await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation activateByClientToken($activateByClientTokenInput: ActivateByClientTokenInput!) {
                activateByClientToken(activateByClientTokenInput: $activateByClientTokenInput)
            }`,
            variables: {
                activateByClientTokenInput: {email: email, token: token},
            },
        });
    },

    async requestClientRegisterToken(email) {
        return await Backoffice.graphQLClient.mutate({
            mutation: gql`mutation requestClientRegisterToken($requestClientRegisterTokenInput: RequestClientRegisterTokenInput!) {
                requestClientRegisterToken(requestClientRegisterTokenInput: $requestClientRegisterTokenInput)
            }`,
            variables: {
                requestClientRegisterTokenInput: {email: email},
            },
        });
    },

    async findLineRides(start_point, end_point, departure_date, currency, passengers = null, adults_count = null) {
        adults_count = passengers ? passengers.filter(i => i.type === 'ADULT').length : adults_count;
        const findLineRidesInput = {
            'startStopPointId': start_point.stopPointId,
            'destinationStopPointId': end_point.stopPointId,
            'rideDate': departure_date,
            'passengers': [{'type': 'ADULT', 'count': adults_count},{'type': 'CHILD_BELOW_4', 'count': passengers?passengers.length-adults_count:0}].filter(i => i.count > 0),
            'additionalOptions': [],
            'value': {currency: currency},
        };
        const response = await Backoffice.graphQLClient.query({
            query: gql`query findLineRides($findLineRidesInput: FindLineRidesInput!) {
                findLineRides(findLineRidesInput: $findLineRidesInput) {
                    lineId
                    rideId
                    startDate
                    endDate
                    startDateFormatted
                    endDateFormatted
                    startStopPointId
                    destinationStopPointId
                    price{grossPrice}
                }
            }`,
            variables: {
                findLineRidesInput: findLineRidesInput,
            },
        });
        return response.data.findLineRides;
    },

    async voucherByClient(code, type, passengersList, orderValue) {
        const voucherByClientInput = {
            'code': code,
            'rideType': type,
            'passengersList': passengersList,
            'orderValue': orderValue,
            'orderCurrency': getCurrency(),
        };
        const response = await Backoffice.graphQLClient.query({
            query: gql`query voucherByClient($voucherByClientInput: VoucherByClientInput!) {
                voucherByClient(voucherByClientInput: $voucherByClientInput) {
                    id,valueAmount,valuePercentage,type
                }
            }`,
            variables: {
                voucherByClientInput: voucherByClientInput,
            },
        });
        return response.data.voucherByClient;
    },

    async stopPoints() {
        const result = await Backoffice.graphQLClient.query({
            query: gql`query stopPoints {
                stopPoints {
                    id
                    name
                }
            }`,
        });
        return result.data.stopPoints;
    },

    async additionalOptions(type = null) {
        let result = await Backoffice.graphQLClient.query({
            query: gql`query additionalOptionsByClient($currency: CURRENCY!, $language: LANGUAGE!) {
                additionalOptionsByClient(currency: $currency,language: $language) {
                    id
                    category {
                        name
                    }
                    price,
                    currency,
                    name,
                    rideTypes
                }
            }`,
            variables: {
                currency: getCurrency(),
                language: getLanguage(),
            },
        });
        result = result.data.additionalOptionsByClient;
        if(type !== null){
            return result.filter(o => o.rideTypes.includes(type));
        }
        return result;
    },

    async findPreferredVehiclesPrices(ride_type, currency, start, end, ride_date) {
        const result = await Backoffice.graphQLClient.query({
            query: gql`query findPreferredVehiclesPrices($findPreferredVehiclesPricesInput: FindPreferredVehiclesPricesInput!) {
                findPreferredVehiclesPrices(findPreferredVehiclesPricesInput: $findPreferredVehiclesPricesInput) {
                    rideType
                    vehicles {
                        vehicleId
                        vehicleInfo{name,description,passengersCount,luggageCount,image{id}}
                        value{grossPrice,vat,netPrice}
                    }
                }
            }`,
            variables: {
                findPreferredVehiclesPricesInput: {
                    'rideType': ride_type,
                    'rideCyclic': null,
                    'rides': [{
                        'stops': [{
                            'addressLatitude': start.lat,
                            'addressLongitude': start.lng,
                            'date': ride_date,
                        },
                        {
                            'addressLatitude': end.lat,
                            'addressLongitude': end.lng,
                        }],
                        'additionalOptions': [],
                    }],
                    'value': {'currency': currency},
                },
            },
        });
        return result.data.findPreferredVehiclesPrices;
    },

    async findAvailableLinesForClientOrder(departure_date = null) {
        const variables = {};
        if(departure_date){
            variables['date'] = departure_date + ' 00:00';
        }
        const result = await Backoffice.graphQLClient.query({
            query: gql`query findAvailableLinesForClientOrder($date: DateTimeOrTimestamp) {
                findAvailableLinesForClientOrder(date: $date) {
                    id
                    name
                    lineStopPoints {
                        id
                        stopPointNumber
                        stopPoint {id,name,nameForClient,category,address,locationLatitude,locationLongitude,cityCode,postalCode,status,comment}
                        stopPointId
                        stopPointStatus
                        stopPointEventList
                        stopPointTravelTime
                    }
                }
            }`,
            variables: variables,
        });
        return result.data.findAvailableLinesForClientOrder;
    },

    async calculateOrderValueByClient(type, passengers, stops) {
        const calculateOrderValueInput = {
            rideType: type,
            rideCharacter: null,
            rideCyclic: null,
            passengers: passengers,
            rides: [{stops: stops, additionalOptions: []}],
            value: {currency: getCurrency()},
        };
        const result = await Backoffice.graphQLClient.query({
            query: gql`query calculateOrderValueByClient($calculateOrderValueInput: CalculateOrderValueInput!) {
                calculateOrderValueByClient(calculateOrderValueInput: $calculateOrderValueInput) {
                    currency
                    value {
                        grossPriceWithoutDiscount
                        grossPriceWithDiscount
                        vat
                        discountValue
                    }
                    additionalOptionsValues {
                        id
                        count
                        grossPrice
                    }
                    baseValue {
                        grossPrice
                    }
                }
            }`,
            variables: {calculateOrderValueInput: calculateOrderValueInput},
        });
        return result.data.calculateOrderValueByClient;
    },

    getAOName(name){
        return name;
    },

    async mapAdditionalOptions(aType, aOptions) {
        const APIoptions = (await this.additionalOptions()).filter(i => i.rideTypes.indexOf(aType) > -1);
        const optionsUsed = aOptions.filter(i => i.quantity > 0);
        return APIoptions.filter(i => !!optionsUsed.filter(j => i.name === this.getAOName(j.label)).length).map(i => {
            return {
                'id': i.id,
                'count': optionsUsed.filter(j => i.name === this.getAOName(j.label))[0].quantity,
            };
        });
    },

    mapAddress(data, coordinates, departure_date) {
        const zip = data.zip?' ' + data.zip:'';
        const city = data.city?' ' + data.city:'';
        return {
            addressAddress: /Berlin Brandenburg/i.test(data.street)?'BERLIN_AIRPORT':(data.street + (city || zip ? ',' + zip + city : '')),
            addressCityCode: data.city?data.city.substring(0,3):null,
            addressPostalCode: data.zip?data.zip:null,
            addressLatitude: coordinates?coordinates.lat:null,
            addressLongitude: coordinates?coordinates.lng:null,
            date: departure_date?departure_date:null,
        };
    },

    mapFlightDate(flightTimeString, departureDateString, destination_is_airport) {
        if(!flightTimeString || !departureDateString){
            return null;
        }
        const departureDateTimestamp = parseInt(getTimestampInTimeZone(departureDateString), 10);
        let flightDateStringTimestamp = parseInt(getTimestampInTimeZone(departureDateString.replace(/\s[0-9]{2}:[0-9]{2}$/, ' ' + flightTimeString)), 10);
        if(flightDateStringTimestamp < departureDateTimestamp && destination_is_airport){
            //add 1 day in s to the date
            flightDateStringTimestamp += 24*60*60;
        }
        return moment(flightDateStringTimestamp, 'X').format('YYYY-MM-DD') + ' ' + flightTimeString;
    },

    mapVehicleTypeEnum(passenger_count) {
        if (passenger_count > 50) {
            return 'UP_TO_60';
        } else if (passenger_count > 37) {
            return 'UP_TO_50';
        } else if (passenger_count > 35) {
            return 'UP_TO_37';
        } else if (passenger_count > 23) {
            return 'UP_TO_35';
        } else if (passenger_count > 20) {
            return 'UP_TO_23';
        } else if (passenger_count > 18) {
            return 'UP_TO_20';
        } else if (passenger_count > 8) {
            return 'UP_TO_18';
        } else if (passenger_count > 7) {
            return 'UP_TO_8';
        } else if (passenger_count > 3) {
            return 'UP_TO_7';
        } else {
            return 'PASSENGER_UP_TO_3';
        }
    },
};
